import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Check, Clock, CircleCheck, CalendarCheck, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { key: "received", label: "تم استلام الطلب", icon: Check },
  { key: "under_review", label: "قيد المراجعة", icon: Clock },
  { key: "accepted", label: "مقبول — بانتظار التنسيق", icon: CircleCheck },
  { key: "confirmed", label: "تم تأكيد الموعد", icon: CalendarCheck },
];

const STATUS_INDEX = { received: 0, under_review: 1, accepted: 2, confirmed: 3 };

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("ar-SA", { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const KhutaSadaTrackerDialog = ({ email, trigger, onReset }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const fetchStatus = async () => {
    if (!email) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/khuta-sada/status`, { params: { email } });
      setData(res.data);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 404) {
        setError("لم نعثر على طلب مسجّل بهذا الإيميل.");
      } else {
        setError("تعذّر تحميل حالة الطلب. حاولي بعد قليل.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchStatus();
    // eslint-disable-next-line
  }, [open]);

  const isRejected = data?.status === "rejected";
  const currentIndex = data ? STATUS_INDEX[data.status] ?? 0 : 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="khuta-tracker-dialog">
        <DialogHeader>
          <DialogTitle className="text-right text-2xl font-bold text-[#0B132B]" style={{ fontFamily: "Cairo" }}>
            تتبع طلبي — «للخطى صدى»
          </DialogTitle>
          <DialogDescription className="text-right text-[#475569]">
            متابعة حالة طلبك خطوة بخطوة.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-10 flex justify-center">
            <Loader2 size={32} className="animate-spin text-[#1E3A8A]" />
          </div>
        )}

        {!loading && error && (
          <div className="py-6 text-center">
            <XCircle size={40} className="text-rose-500 mx-auto mb-3" />
            <p className="text-[#475569] mb-4">{error}</p>
            <Button
              variant="outline"
              onClick={() => {
                if (onReset) onReset();
                setOpen(false);
              }}
              data-testid="khuta-tracker-reset"
            >
              تسجيل طلب جديد
            </Button>
          </div>
        )}

        {!loading && !error && data && (
          <div className="space-y-6 mt-2" data-testid="khuta-tracker-content">
            {/* Summary */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-[#64748B] mb-1">عنوان الطلب</p>
                  <p className="text-[#0B132B] font-semibold">{data.workshop_title}</p>
                </div>
                {data.is_waitlist && !isRejected && (
                  <span
                    className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold"
                    style={{ background: "#F5C86B", color: "#083344" }}
                    data-testid="khuta-tracker-waitlist-badge"
                  >
                    ⏳ قائمة الانتظار
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                <div>
                  <p className="text-[#64748B]">تاريخ الزيارة</p>
                  <p className="text-[#0B132B] font-medium">{data.visit_date}</p>
                </div>
                <div>
                  <p className="text-[#64748B]">آخر تحديث</p>
                  <p className="text-[#0B132B] font-medium">{formatDate(data.updated_at)}</p>
                </div>
              </div>
              {data.is_waitlist && !isRejected && (
                <p className="mt-3 text-xs leading-relaxed rounded-md p-2.5" style={{ background: "#FFFBEB", color: "#92400E", border: "1px solid #F5C86B" }}>
                  التاريخ محجوز حاليًا. ستتم ترقيتكِ تلقائيًا إذا تحرّر المقعد، وسيصلكِ بريد تأكيد.
                </p>
              )}
            </div>

            {isRejected ? (
              <div className="border border-rose-200 bg-rose-50 rounded-lg p-5 text-center">
                <XCircle size={44} className="text-rose-600 mx-auto mb-3" />
                <h3 className="text-lg font-bold text-rose-900 mb-2">نعتذر لم يتم قبول الطلب</h3>
                {data.admin_note && (
                  <p className="text-sm text-rose-800 whitespace-pre-wrap bg-white/60 rounded p-3 mt-2">{data.admin_note}</p>
                )}
              </div>
            ) : (
              <>
                {/* Vertical stepper */}
                <div className="relative">
                  {STEPS.map((step, idx) => {
                    const isDone = idx < currentIndex;
                    const isCurrent = idx === currentIndex;
                    const Icon = step.icon;
                    const historyEntry = (data.status_history || []).find((s) => s.status === step.key);
                    return (
                      <div key={step.key} className="flex gap-4 pb-6 relative">
                        {/* Connector line */}
                        {idx < STEPS.length - 1 && (
                          <div
                            className="absolute top-10 right-5 w-0.5 h-full"
                            style={{ backgroundColor: isDone ? "#10B981" : "#E2E8F0" }}
                          />
                        )}
                        {/* Circle */}
                        <div
                          className="relative shrink-0 w-10 h-10 rounded-full flex items-center justify-center z-10 transition-all"
                          style={{
                            backgroundColor: isDone ? "#10B981" : isCurrent ? "#1E3A8A" : "#F1F5F9",
                            color: isDone || isCurrent ? "#fff" : "#94A3B8",
                            boxShadow: isCurrent ? "0 0 0 4px rgba(30,58,138,0.15)" : "none",
                          }}
                        >
                          <Icon size={18} />
                        </div>
                        {/* Content */}
                        <div className="flex-1 pt-1">
                          <p
                            className="font-semibold"
                            style={{ color: isDone || isCurrent ? "#0B132B" : "#94A3B8", fontFamily: "Cairo" }}
                          >
                            {step.label}
                          </p>
                          {historyEntry && (
                            <p className="text-xs text-[#64748B] mt-1">{formatDate(historyEntry.at)}</p>
                          )}
                          {isCurrent && data.admin_note && (
                            <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900 whitespace-pre-wrap">
                              <strong>ملاحظة من المدرسة:</strong> {data.admin_note}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <div className="flex justify-between items-center pt-2 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchStatus}
                data-testid="khuta-tracker-refresh"
              >
                تحديث
              </Button>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm("هل تريدين إزالة الرابط من هذا الجهاز؟ يمكنك دائماً استعادته بإدخال إيميلك.")) {
                    if (onReset) onReset();
                    setOpen(false);
                  }
                }}
                className="text-xs text-[#64748B] hover:text-rose-600"
                data-testid="khuta-tracker-forget"
              >
                إزالة التتبع من هذا الجهاز
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default KhutaSadaTrackerDialog;
